import { createReducer, on } from '@ngrx/store';

import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';

import {
  DataVisualizationState,
  FilterMethod,
  MapFilterType
} from '../../models/data-visualization/data-visualization.state';
import { AuxiliaryDataContext } from '../../services/auxiliary-data/auxiliary-data.service';
import * as DataVisualizationActions from './data-visualization.actions';

export const initialDataVisualizationState: DataVisualizationState = {
  openedVisualizationWindows: {
    map: true,
    calendar: false,
    timeline: false,
    list: false,
    occupation: false
  },

  filterOnAggregator: {
    filteredAggregatorIds: [MapFilterType.RESET],
    filterMethod: FilterMethod.DISABLED,
    markerId: undefined,
    focusedActionUid: undefined
  },

  isHydrating: false,

  componentStates: {
    availableVisualizations: new Set<VisualizationModule>([]),
    mapVisualization: {
      markers: [],
      routes: [],
      areas: []
    },
    listVisualization: {
      nbElems: 0,
      lists: []
    },
    timelineVisualization: {
      rows: []
    },
    calendarVisualization: {
      events: []
    }
  },

  occupationVisualization: {
    series: [],
    loading: false,
    siteUidsInView: [],
    requestedSiteUids: []
  },

  visualizationContext: null,

  timelineModificationDescriptors: {},

  timelineTimeStep: null
};

/** Two contexts are the same query scope, so occupation fetched under one holds under the other. */
function sameContext(
  a: AuxiliaryDataContext | null,
  b: AuxiliaryDataContext | null
): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.dataSpaceId === b.dataSpaceId &&
    a.planId === b.planId &&
    a.planOperator === b.planOperator
  );
}

export const dataVisualizationReducer = createReducer(
  initialDataVisualizationState,

  on(DataVisualizationActions.setVisualizationHydrating, (state, { hydrating }) => ({
    ...state,
    isHydrating: hydrating
  })),

  // Occupation is keyed by dataspace and plan, so a change of either makes what is loaded
  // answer a question nobody asked: plan A's docks would stay on screen under plan B.
  on(DataVisualizationActions.setVisualizedEntities, (state, { auxContext }) => {
    const context = auxContext ?? null;
    if (sameContext(state.visualizationContext, context)) return state;
    return {
      ...state,
      visualizationContext: context,
      occupationVisualization: initialDataVisualizationState.occupationVisualization
    };
  }),

  on(DataVisualizationActions.setOccupationSitesInView, (state, { siteUids }) => ({
    ...state,
    occupationVisualization: { ...state.occupationVisualization, siteUidsInView: siteUids }
  })),

  on(DataVisualizationActions.occupationRequested, (state, { siteUids }) => ({
    ...state,
    occupationVisualization: {
      ...state.occupationVisualization,
      requestedSiteUids: [
        ...new Set([...state.occupationVisualization.requestedSiteUids, ...siteUids])
      ]
    }
  })),

  on(DataVisualizationActions.setOccupationSeries, (state, { series }) => ({
    ...state,
    occupationVisualization: {
      ...state.occupationVisualization,
      series,
      loading: false
    }
  })),

  on(DataVisualizationActions.setOccupationLoading, (state, { loading }) => ({
    ...state,
    occupationVisualization: { ...state.occupationVisualization, loading }
  })),

  on(DataVisualizationActions.setVisuComponentStates, (state, { states }) => ({
    ...state,
    componentStates: states,
    isHydrating: false,
    timelineTimeStep: null
  })),

  on(DataVisualizationActions.setGaliaMarkers, (state, { markers }) => ({
    ...state,
    mapVisualization: {
      ...state.componentStates.mapVisualization,
      markers
    }
  })),

  on(DataVisualizationActions.setGaliaRoutes, (state, { routes }) => ({
    ...state,
    mapVisualization: {
      ...state.componentStates.mapVisualization,
      routes
    }
  })),

  on(DataVisualizationActions.setTimelineRows, (state, { rows }) => ({
    ...state,
    timelineVisualization: {
      ...state.componentStates.timelineVisualization,
      rows
    }
  })),

  on(DataVisualizationActions.setVisualizationLists, (state, { lists }) => ({
    ...state,
    listVisualization: {
      ...state.componentStates.listVisualization,
      lists
    }
  })),

  on(DataVisualizationActions.setCalendarEvents, (state, { events }) => ({
    ...state,
    calendarVisualization: {
      ...state.componentStates.calendarVisualization,
      events
    }
  })),

  on(
    DataVisualizationActions.setFilterOnAggregator,
    (state, { filteredAggregatorIds, filterMethod, markerId }) => ({
      ...state,
      filterOnAggregator: {
        filteredAggregatorIds,
        filterMethod,
        markerId,
        // DISABLED is a reset and clears everything, including a job pin. Every other
        // filter method is not a reset — pinning or filtering a route must leave an
        // action focus raised earlier alone, or a route click would silently undo it.
        focusedActionUid:
          filterMethod === FilterMethod.DISABLED
            ? undefined
            : state.filterOnAggregator.focusedActionUid
      }
    })
  ),

  on(DataVisualizationActions.setActionFocus, (state, { actionUid }) => ({
    ...state,
    filterOnAggregator: {
      ...state.filterOnAggregator,
      focusedActionUid: actionUid ?? undefined
    }
  })),

  on(DataVisualizationActions.setDataVisualizationState, (state, { newState }) => ({
    ...state,
    ...newState
  })),

  on(DataVisualizationActions.setTimelineTimeStep, (state, { timeStep }) => ({
    ...state,
    timelineTimeStep: timeStep
  })),

  on(
    DataVisualizationActions.setTimelineModificationDescriptors,
    (state, { descriptors }) => ({
      ...state,
      timelineModificationDescriptors: descriptors
    })
  ),

  on(DataVisualizationActions.toggleCalendarVisualization, (state, { isOpen }) => ({
    ...state,
    openedVisualizationWindows: {
      ...state.openedVisualizationWindows,
      calendar: isOpen
    }
  })),

  on(DataVisualizationActions.toggleListVisualization, (state, { isOpen }) => ({
    ...state,
    openedVisualizationWindows: {
      ...state.openedVisualizationWindows,
      list: isOpen
    }
  })),

  on(DataVisualizationActions.toggleTimelineVisualization, (state, { isOpen }) => ({
    ...state,
    openedVisualizationWindows: {
      ...state.openedVisualizationWindows,
      timeline: isOpen
    }
  })),

  on(DataVisualizationActions.toggleMapVisualization, (state, { isOpen }) => ({
    ...state,
    openedVisualizationWindows: {
      ...state.openedVisualizationWindows,
      map: isOpen
    }
  })),

  on(DataVisualizationActions.toggleOccupationVisualization, (state, { isOpen }) => ({
    ...state,
    openedVisualizationWindows: {
      ...state.openedVisualizationWindows,
      occupation: isOpen
    }
  }))
);
